/* eslint-disable react/prop-types */
// /components/TextEffects/TextEffectScramble.js
import React, { useEffect, useState, useRef } from 'react'
import './text-effects.css'

// Character pools for intelligent replacements
const POOLS = {
	UPPER: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
	LOWER: 'abcdefghijklmnopqrstuvwxyz',
	NUMBER: '0123456789'
}

// Get a random character matching the type of the original char
const getRandomChar = (originalChar) => {
	if (!originalChar) return originalChar
	if (/[A-Z]/.test(originalChar))
		return POOLS.UPPER[Math.floor(Math.random() * POOLS.UPPER.length)]
	if (/[a-z]/.test(originalChar))
		return POOLS.LOWER[Math.floor(Math.random() * POOLS.LOWER.length)]
	if (/[0-9]/.test(originalChar))
		return POOLS.NUMBER[Math.floor(Math.random() * POOLS.NUMBER.length)]
	// Preserve symbols, spaces, punctuation, etc.
	return originalChar
}

export const TextEffectScramble = ({
	children,
	duration = 1000, // total duration in ms
	interval = 40, // update frequency
	delay = 0, // optional delay before start
	scrambleOn = 'viewport', // 'hover' | 'viewport' | 'instant'
	hoverTarget = 'parent', // 'parent' (default) or 'self'
	startAfter = 3, // number of characters left untouched initially
	style = {},
	className = ''
}) => {
	const [displayed, setDisplayed] = useState('')
	const containerRef = useRef(null)
	const [isActive, setIsActive] = useState(scrambleOn === 'instant')
	const [triggerCount, setTriggerCount] = useState(0)

	// Extract plain text content
	const extractText = (nodes) => {
		let text = ''
		React.Children.forEach(nodes, (child) => {
			if (typeof child === 'string') text += child
			else if (child?.props?.children)
				text += extractText(child.props.children)
		})
		return text
	}
	const fullText = extractText(children)

	// ✅ Trigger when element enters viewport
	useEffect(() => {
		if (scrambleOn !== 'viewport' || !containerRef.current) return
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry.isIntersecting) {
					setTimeout(() => {
						setTriggerCount((p) => p + 1)
						setIsActive(true)
					}, delay)
				}
			},
			{ threshold: 0.25 }
		)
		observer.observe(containerRef.current)
		return () => observer.disconnect()
	}, [scrambleOn, delay])

	// ✅ Hover trigger (supports parent or self)
	useEffect(() => {
		if (scrambleOn !== 'hover' || !containerRef.current) return

		const el =
			hoverTarget === 'parent'
				? containerRef.current.parentElement
				: containerRef.current

		if (!el) return
		const onEnter = () => {
			setTriggerCount((p) => p + 1)
			setIsActive(true)
		}
		el.addEventListener('mouseenter', onEnter)
		return () => el.removeEventListener('mouseenter', onEnter)
	}, [scrambleOn, hoverTarget])

	// ✅ Scramble logic — only letters & digits are replaced
	useEffect(() => {
		if (!isActive || !fullText) return

		const totalChars = fullText.length
		const protectedChars = Math.min(startAfter, totalChars)
		const steps = Math.ceil(duration / interval)
		let step = 0

		const timer = setInterval(() => {
			step++
			const progress = Math.min(step / steps, 1)
			const revealed = Math.floor(
				progress * (totalChars - protectedChars)
			)

			let result = ''
			for (let i = 0; i < totalChars; i++) {
				const originalChar = fullText[i]
				if (i < protectedChars || i < protectedChars + revealed) {
					result += originalChar
				} else {
					result += getRandomChar(originalChar)
				}
			}

			setDisplayed(result)

			if (progress >= 1) {
				clearInterval(timer)
				setDisplayed(fullText)
				setIsActive(false)
			}
		}, interval)

		return () => clearInterval(timer)
	}, [triggerCount])

	return (
		<span
			ref={containerRef}
			className={`we-text-scramble ${className}`}
			style={{ display: 'inline-block', ...style }}
		>
			{displayed || fullText}
		</span>
	)
}
